import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import {
  useAdditionalTradingFormContext,
  useTradingFormContext
} from '../trading-form-context';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormArrayItem } from '@client/foundation/form/form-array-item';
import { FormContainer } from '@client/foundation/layout/form-container';
import { TradingFormField } from '../trading-form-field';
import {
  SignalCondition,
  TimeframeConfigInput
} from '@server/data-feed/schemas/input.schema';
import { timeframes } from '../../chart/constants/tradng-form-defaults';
import { FieldWrapper } from '@client/foundation/layout/field-wrapper';
import { UploadFile } from '@mui/icons-material';
import { FC, useEffect } from 'react';
import { extractSymbolAndTimeframe } from '../../chart/utils/file.util';
import { FormConfigButtons } from '../form-config-buttons';
import { Section } from '@client/foundation/layout/section';
import { getIndicatorsPath } from '../indicators/indicator-config-form.utils';
import { IndicatorConfigForm } from '../indicators/indicator-config-form';
import { StrategyConfigForm } from '../strategy/strategy-config-form.component';
import { getStrategyPath } from '../strategy/strategy-config-form.utils';
import {
  borderStyle,
  sectionPanelStyle,
  sectionHeaderPanelStyle
} from '@client/foundation/layout/styling';
import { UiStatePath } from '../trading-form-ui-state.hook';
import { AppIconButton } from '@client/foundation/design-system/components';
import { colorTokens } from '@client/foundation/design-system/tokens';

type TimeframesFormProps = {};

export const TimeframesForm: FC<TimeframesFormProps> = () => {
  const { control, watch } = useTradingFormContext();
  const { setUiState, getUiState } = useAdditionalTradingFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'timeframes'
  });

  const timeframeState = watch('timeframes') || [];

  return (
    <Box>
      {fields.map((field, index) => {
        const timeframe = timeframeState[index].timeframe;
        const path: UiStatePath = ['sections', 'timeframes', 'items', index];
        const expanded = getUiState(path, { expanded: true }).expanded;

        return (
          <Section
            key={field.id}
            sx={sectionHeaderPanelStyle}
            title={timeframe}
            collapsible={true}
            expanded={expanded}
            onToggle={(expanded) => {
              const current = getUiState<Record<string, unknown>>(path, {});
              setUiState(path, { ...current, expanded });
            }}
          >
            <FormArrayItem key={field.id} index={index} onRemove={remove}>
              <TimeframeFormItem index={index} />
            </FormArrayItem>
          </Section>
        );
      })}
      <AppIconButton
        tone="primary"
        title="Add timeframe"
        aria-label="Add timeframe"
        onClick={() => append({} as TimeframeConfigInput)}
      >
        <AddIcon />
      </AppIconButton>
    </Box>
  );
};

type TimeframeFormItemProps = {
  index: number;
};

export const TimeframeFormItem: FC<TimeframeFormItemProps> = ({ index }) => {
  const { control, setValue, watch } = useTradingFormContext();
  const { setUiState, getUiState } = useAdditionalTradingFormContext();
  const { tradingMode } = watch('marketConfig') || {};
  const { useExchangeData } =
    useWatch({
      control,
      name: 'marketConfig'
    }) || {};

  const { file } = useWatch({ control, name: `timeframes.${index}` }) || {};

  useEffect(() => {
    if (!file) return;

    const { timeframe } = extractSymbolAndTimeframe(file.name);

    if (timeframe) {
      setValue(`timeframes.${index}.timeframe`, timeframe.toLowerCase());
    }
  }, [file, index, setValue]);

  const indicatorsPath: UiStatePath = [
    'sections',
    'timeframes',
    'items',
    index,
    'indicators'
  ];
  const indicatorsFormPath = getIndicatorsPath(index);
  const indicatorsDetailsPath: UiStatePath = [
    'sections',
    'timeframes',
    'items',
    index,
    'indicators',
    'details'
  ];
  const strategyPath: UiStatePath = [
    'sections',
    'timeframes',
    'items',
    index,
    'strategy'
  ];
  const strategyFormPath = getStrategyPath(index);

  return (
    <Box sx={{ width: '100%' }}>
      <FormContainer>
        <TradingFormField
          type="select"
          name={`timeframes.${index}.timeframe`}
          label="Timeframe"
          options={timeframes}
          defaultValue="4h"
          wrapperProps={{ sx: { mb: 4 } }}
        />
        {tradingMode === 'BACKTESTING' && !useExchangeData && (
          <FieldWrapper>
            <Button
              component="label"
              variant="outlined"
              size="large"
              startIcon={<UploadFile />}
              sx={{ mb: 0.5 }}
            >
              Select File
              <input
                type="file"
                hidden
                onChange={(e) =>
                  setValue(`timeframes.${index}.file`, e.target.files?.[0])
                }
              />
            </Button>
            <Controller
              name={`timeframes.${index}.file`}
              control={control}
              render={({ field }) =>
                field.value ? (
                  <Tooltip title={field.value.name}>
                    <Typography variant="body2">{field.value.name}</Typography>
                  </Tooltip>
                ) : (
                  // Ensure we return a valid React element even if the value is null
                  <Typography variant="body2">No file selected</Typography>
                )
              }
            />
          </FieldWrapper>
        )}

        <TradingFormField
          type="numeric"
          name={`timeframes.${index}.historicalDays`}
          label="Historical Days"
        />
      </FormContainer>

      <Section
        title="Indicators"
        actionButtons={
          <FormConfigButtons
            templateType="indicators"
            timeframeIndex={index}
          />
        }
        sx={{ ...sectionPanelStyle, ...borderStyle }}
        collapsible={true}
        expanded={getUiState(indicatorsPath, { expanded: true }).expanded}
        onToggle={(expanded) => {
          const current = getUiState<Record<string, unknown>>(
            indicatorsPath,
            {}
          );
          setUiState(indicatorsPath, { ...current, expanded });
        }}
      >
        <Section
          title="Configured indicators"
          sx={{
            ...sectionHeaderPanelStyle,
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: colorTokens.whiteAlpha22
          }}
          collapsible={true}
          expanded={
            getUiState(indicatorsDetailsPath, { expanded: true }).expanded
          }
          onToggle={(expanded) => {
            const current = getUiState<Record<string, unknown>>(
              indicatorsDetailsPath,
              {}
            );
            setUiState(indicatorsDetailsPath, { ...current, expanded });
          }}
        >
          <IndicatorConfigForm
            indicatorsPath={indicatorsFormPath}
            timeframeIndex={index}
          />
        </Section>
      </Section>
      <Section
        title="Strategy"
        actionButtons={
          <FormConfigButtons
            templateType="strategy"
            timeframeIndex={index}
          />
        }
        sx={{ ...sectionPanelStyle, ...borderStyle }}
        collapsible={true}
        expanded={getUiState(strategyPath, { expanded: true }).expanded}
        onToggle={(expanded) => {
          const current = getUiState<Record<string, unknown>>(strategyPath, {});
          setUiState(strategyPath, { ...current, expanded });
        }}
      >
        <StrategyConfigForm
          strategyPath={strategyFormPath}
          timeframeIndex={index}
        />
      </Section>
    </Box>
  );
};
